import { motion, useInView } from "motion/react";
import { useRef, useState, useEffect, forwardRef } from "react";
import { ExternalLink, Menu, X } from "lucide-react";
import { PageMeta } from "../PageMeta";
import { Breadcrumbs } from "../Breadcrumbs";

export function DocsPage() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("quickstart");

  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true, amount: 0.1 });

  // Refs for sections to track scroll position. Typed to HTMLElement (the
  // element DocSection forwards) so `current.getBoundingClientRect()` resolves.
  const quickstartRef = useRef<HTMLElement | null>(null);
  const sdksRef = useRef<HTMLElement | null>(null);
  const policyRef = useRef<HTMLElement | null>(null);
  const auditRef = useRef<HTMLElement | null>(null);
  const liveTraceRef = useRef<HTMLElement | null>(null);
  const reviewQueueRef = useRef<HTMLElement | null>(null);
  const nodejsRef = useRef<HTMLElement | null>(null);
  const pythonRef = useRef<HTMLElement | null>(null);

  // Update active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        { id: "quickstart", ref: quickstartRef },
        { id: "sdks-api", ref: sdksRef },
        { id: "policy-routing", ref: policyRef },
        { id: "audit-logs", ref: auditRef },
        { id: "live-trace", ref: liveTraceRef },
        { id: "review-queue", ref: reviewQueueRef },
        { id: "nodejs-sdk", ref: nodejsRef },
        { id: "python-sdk", ref: pythonRef },
      ];

      for (const section of sections) {
        const element = section.ref.current;
        if (element) {
          const rect = element.getBoundingClientRect();
          // Check if section is in viewport (top 30% of screen)
          if (rect.top <= 200 && rect.bottom >= 200) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Smooth scroll to section
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100; // Offset for better positioning
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: "smooth",
      });
      setIsSidebarOpen(false); // Close mobile sidebar after navigation
    }
  };

  // Close sidebar on desktop resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const navLinks = [
    { id: "quickstart", label: "Quickstart" },
    { id: "sdks-api", label: "Confidence Engine API" },
    { id: "policy-routing", label: "Policy Routing" },
    { id: "audit-logs", label: "Audit Logs" },
    { id: "live-trace", label: "Live Trace Stream" },
    { id: "review-queue", label: "Human Review Queue" },
    { id: "nodejs-sdk", label: "Node.js SDK Reference" },
    { id: "python-sdk", label: "Python SDK Reference" },
  ];

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      <PageMeta
        title="Documentation | API Reference & Integration Guides"
        description="Complete ADJUDON documentation. API reference, SDK guides, integration examples, and compliance playbooks."
        keywords="API documentation, integration guides, SDK reference, compliance playbooks, technical docs, developer docs"
        ogImage="/og-docs.png"
      />
      
      {/* Mobile Sidebar Toggle (fixed top-right) */}
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="fixed top-6 right-6 z-50 lg:hidden p-3 border border-[var(--border)] bg-[var(--surface-1)] transition-colors duration-200 hover:bg-[var(--surface-2)]"
        aria-label="Toggle sidebar"
      >
        {isSidebarOpen ? (
          <X className="w-5 h-5" />
        ) : (
          <Menu className="w-5 h-5" />
        )}
      </button>

      <div className="flex">
        {/* SIDEBAR */}
        <aside
          className={`
            fixed lg:sticky top-0 left-0 h-screen w-64 border-r border-[var(--border)] bg-[var(--surface-1)] z-40
            transition-transform duration-300 ease-in-out
            ${isSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
            overflow-y-auto
          `}
        >
          {/* Top Accent Line */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-[var(--system-cyan)]" />
          
          <div className="p-6 pt-8">
            {/* Sidebar Header */}
            <h2 className="font-mono tracking-[0.2em] text-[var(--muted-foreground)] mb-8" >
              Documentation
            </h2>

            {/* Navigation Links */}
            <nav className="space-y-2 mb-12">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className={`
                    w-full text-left px-4 py-2.5 text-sm transition-colors duration-200
                    ${
                      activeSection === link.id
                        ? "text-[var(--system-cyan)] bg-[var(--system-cyan)]/10 border-l-2 border-[var(--system-cyan)]"
                        : "text-[var(--foreground)] hover:text-[var(--system-cyan)] hover:bg-[var(--surface-2)] border-l-2 border-transparent"
                    }
                  `}
                  style={{
                    letterSpacing: "-0.01em",
                    lineHeight: "1.4",
                  }}
                >
                  {link.label}
                </button>
              ))}
            </nav>

            {/* Footer Links */}
            <div className="space-y-3 pt-8 border-t border-[var(--border)]">
              <a
                href="https://github.com/Adjudon"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-[var(--muted-foreground)] transition-colors duration-200 hover:text-[var(--system-cyan)]"
              >
                GitHub
                <ExternalLink className="w-4 h-4" />
              </a>
              <a
                href="/contact"
                className="flex items-center gap-2 text-sm text-[var(--muted-foreground)] transition-colors duration-200 hover:text-[var(--system-cyan)]"
              >
                Engineer Support
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </aside>

        {/* MAIN CONTENT */}
        <main className="flex-1 lg:ml-0 min-w-0">
          {/* Hero Section */}
          <section ref={heroRef} className="relative border-b border-[var(--border)] section-padding">
            <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-radial from-[var(--premium)]/5 via-transparent to-transparent pointer-events-none" />

            <div className="mx-auto relative z-10 page-responsive">
              {/* Breadcrumbs */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                className="mb-8"
              >
                <Breadcrumbs
                  items={[
                    { label: "Documentation", href: "https://docs.adjudon.com" }
                  ]}
                />
              </motion.div>
              
              {/* Eyebrow label */}
              <motion.span
                className="font-mono uppercase tracking-[0.2em] text-[var(--system-cyan)] mb-6 block eyebrow"
                initial={{ opacity: 0, y: 20 }}
                animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
              >
                Documentation
              </motion.span>

              <motion.h1
                className="font-semibold mb-8 h1-hero"
                style={{
                  lineHeight: "1.05",
                  letterSpacing: "-0.03em",
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.8, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
              >
                Documentation
              </motion.h1>

              {/* Intro */}
              <motion.p
                className="text-lg sm:text-xl md:text-2xl text-[var(--muted-foreground)] max-w-4xl"
                style={{
                  lineHeight: "1.6",
                  letterSpacing: "0.01em",
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.8, delay: 0.3, ease: [0.4, 0, 0.2, 1] }}
              >
                Integrate ADJUDON's runtime compliance layer into your AI agent stack. Send decision traces via POST /api/traces or the SDK; the Confidence Engine scores each trace and your policies determine routing. Under 10 minutes to first trace.
              </motion.p>
            </div>
          </section>

          {/* Content Sections */}
          <div className="section-padding mx-auto space-y-20 min-w-0 page-responsive">
            {/* Quickstart Section */}
            <DocSection id="quickstart" title="Quickstart" ref={quickstartRef}>
              <p className="text-base md:text-lg text-[var(--muted-foreground)] mb-8" style={{ lineHeight: "1.7" }}>
                Get started with ADJUDON in three simple steps.
              </p>

              {/* Step 01 */}
              <StepCard
                stepNumber="01"
                title="Install the SDK"
                code="npm install @adjudon/node-sdk"
              />

              {/* Step 02 */}
              <StepCard
                stepNumber="02"
                title="Initialize the client"
                code={`import { AdjudonClient } from '@adjudon/node-sdk';

const client = new AdjudonClient({
  apiKey: process.env.ADJUDON_API_KEY,
});`}
              />

              {/* Step 03 */}
              <StepCard
                stepNumber="03"
                title="Send a decision trace"
                code={`const result = await client.ingestTrace({
  agentId: 'loan-approval-v1',
  inputContext:  { prompt, sessionId },
  outputDecision: { decision: 'approve', confidenceScore: 0.85 },
  alternatives: [{ decision: 'deny', confidence: 0.12 }],
});

// 201 = approved, 202 = flagged for review (trace in queue), 403 = blocked by policy
// result.data.traceId → immutable audit log reference`}
              />
            </DocSection>

            {/* SDKs & API Section */}
            <DocSection id="sdks-api" title="SDKs & API" ref={sdksRef}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Node.js SDK Card */}
                <SDKCard
                  title="Node.js SDK"
                  packageName="@adjudon/node"
                  features={[
                    "TypeScript-first",
                    "Full async/await support",
                    "Fail-open by default",
                  ]}
                />

                {/* Python SDK Card */}
                <SDKCard
                  title="Python SDK"
                  packageName="adjudon-python"
                  features={[
                    "Sync and async clients",
                    "Compatible with LangChain",
                    "Compatible with LlamaIndex",
                  ]}
                />
              </div>

              {/* Info Line */}
              <p
                className="text-sm text-[var(--muted-foreground)] border-l-2 border-[var(--system-cyan)] pl-4"
                style={{ lineHeight: "1.7" }}
              >
                Both SDKs are MIT-licensed and hosted on GitHub. API keys can be scoped by agent and set to read-only for least-privilege access.
              </p>
            </DocSection>

            {/* Policy Routing Section */}
            <DocSection id="policy-routing" title="Policy Routing" ref={policyRef}>
              <p className="text-base md:text-lg text-[var(--muted-foreground)] mb-6" style={{ lineHeight: "1.7" }}>
                Define deterministic rules that dictate how agent responses are handled based on the Confidence Performance Index (CPI) and detected PII markers.
              </p>

              <CodeBlock
                code={`{
  "policy": {
    "cpi_threshold": 0.85,
    "pii_handling": "mask",
    "fallback_action": "route_to_human",
    "required_scopes": ["finance_read"]
  }
}`}
              />
            </DocSection>

            {/* Audit Logs Section */}
            <DocSection id="audit-logs" title="Audit Logs" ref={auditRef}>
              <p className="text-base md:text-lg text-[var(--muted-foreground)] mb-8" style={{ lineHeight: "1.7" }}>
                Every evaluation generates an immutable trace. Use the Audit Log API to list, retrieve, and export traces for regulatory reporting (e.g. EU AI Act Art. 13). Export produces CSV with full context; trace storage supports configurable retention and integrity guarantees.
              </p>

              {/* Endpoint Cards */}
              <div className="space-y-4">
                <EndpointCard
                  method="GET"
                  endpoint="/api/traces"
                  description="List traces with pagination and filters."
                />
                <EndpointCard
                  method="GET"
                  endpoint="/api/traces/:id"
                  description="Retrieve full trace payload."
                />
                <EndpointCard
                  method="GET"
                  endpoint="/api/traces/export"
                  description="Export traces as CSV."
                />
              </div>
            </DocSection>

            {/* Live Trace Stream Section */}
            <DocSection id="live-trace" title="Live Trace Stream" ref={liveTraceRef}>
              <p className="text-base md:text-lg text-[var(--muted-foreground)] mb-4" style={{ lineHeight: "1.7" }}>
                Subscribe to a server-sent events (SSE) stream for real-time decisions:
              </p>
              <CodeBlock code="GET /api/traces/stream" language="http" />
              <p className="text-sm text-[var(--muted-foreground)] mt-4" style={{ lineHeight: "1.7" }}>
                Use it for live dashboards, monitoring walls, or alerting. Heartbeat every 15s; each event includes the full trace payload.
              </p>
            </DocSection>

            {/* Human Review Queue Section */}
            <DocSection id="review-queue" title="Human Review Queue" ref={reviewQueueRef}>
              <p className="text-base md:text-lg text-[var(--muted-foreground)] mb-6" style={{ lineHeight: "1.7" }}>
                When an agent's confidence score falls below your defined threshold, the decision is flagged for the Human Review Queue so your team can approve or override before it reaches the end user.
              </p>

              <ul className="space-y-3">
                {[
                  "Compliance officers can view the full context, prompt, and generated output.",
                  "One-click approval or rejection overrides the agent definitively.",
                  "Webhooks fire immediately upon human resolution to unblock asynchronous flows.",
                  "Review decisions are fed back to improve your custom evaluation policies.",
                ].map((item, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 text-sm md:text-base text-[var(--muted-foreground)]"
                    style={{ lineHeight: "1.7" }}
                  >
                    <span className="w-1.5 h-1.5 bg-[var(--system-cyan)] mt-2.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </DocSection>

            {/* Placeholder Sections for SDK References */}
            <DocSection id="nodejs-sdk" title="Node.js SDK Reference" ref={nodejsRef}>
              <p className="text-base md:text-lg text-[var(--muted-foreground)] mb-6" style={{ lineHeight: "1.7" }}>
                The ADJUDON Node.js SDK provides native TypeScript/JavaScript integration for AI governance and compliance tracking.
              </p>
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-[var(--foreground)] mb-2 uppercase tracking-wider">Installation</h4>
                  <pre className="bg-surface-2 border border-border p-4 text-xs md:text-sm font-mono text-foreground overflow-x-auto">
npm install @adjudon/node-sdk
{'\n'}# or
{'\n'}yarn add @adjudon/node-sdk</pre>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-[var(--foreground)] mb-2 uppercase tracking-wider">Quick Start</h4>
                  <pre className="bg-surface-2 border border-border p-4 text-xs md:text-sm font-mono text-foreground overflow-x-auto">
{`import { AdjudonClient } from '@adjudon/sdk';

const client = new AdjudonClient({
  apiKey: process.env.ADJUDON_API_KEY,
  environment: 'production'
});

// Track agent execution
await client.trace({
  agentId: 'agent_123',
  event: 'completion',
  input: { prompt: 'User query' },
  output: { response: 'Agent response', confidence: 0.95 }
});`}</pre>
                </div>
              </div>
            </DocSection>

            <DocSection id="python-sdk" title="Python SDK Reference" ref={pythonRef}>
              <p className="text-base md:text-lg text-[var(--muted-foreground)] mb-6" style={{ lineHeight: "1.7" }}>
                The ADJUDON Python SDK enables seamless integration with Python-based AI agents and LLM applications.
              </p>
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-[var(--foreground)] mb-2 uppercase tracking-wider">Installation</h4>
                  <pre className="bg-surface-2 border border-border p-4 text-xs md:text-sm font-mono text-foreground overflow-x-auto">
pip install adjudon</pre>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-[var(--foreground)] mb-2 uppercase tracking-wider">Quick Start</h4>
                  <pre className="bg-surface-2 border border-border p-4 text-xs md:text-sm font-mono text-foreground overflow-x-auto">
{`from adjudon import AdjudonClient
import os

client = AdjudonClient(
    api_key=os.getenv('ADJUDON_API_KEY'),
    environment='production'
)

# Track agent execution
client.trace(
    agent_id='agent_123',
    event='completion',
    input={'prompt': 'User query'},
    output={'response': 'Agent response', 'confidence': 0.95}
)`}</pre>
                </div>
              </div>
            </DocSection>
          </div>
        </main>
      </div>
    </div>
  );
}

// DocSection Component with forwardRef
const DocSection = forwardRef<
  HTMLElement,
  {
    id: string;
    title: string;
    children: React.ReactNode;
  }
>(({ id, title, children }, forwardedRef) => {
  // Use internal ref for useInView
  const internalRef = useRef<HTMLElement>(null);
  const isInView = useInView(internalRef, { once: true, amount: 0.05 });

  // Combine refs
  useEffect(() => {
    if (forwardedRef && internalRef.current) {
      if (typeof forwardedRef === 'function') {
        forwardedRef(internalRef.current);
      } else {
        forwardedRef.current = internalRef.current;
      }
    }
  }, [forwardedRef]);

  return (
    <motion.section
      id={id}
      ref={internalRef}
      className="relative border border-[var(--border)] bg-[var(--surface-1)] p-8 md:p-12 mb-8"
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
    >
      {/* Top accent line - ANIMATED */}
      <motion.div
        className="absolute top-0 left-0 right-0 h-[2px] bg-[var(--system-cyan)]"
        style={{ transformOrigin: "left" }}
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: false }}
        transition={{ duration: 0.6, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
      />

      <h2
        className="text-3xl md:text-4xl lg:text-5xl font-semibold mb-6"
        style={{
          lineHeight: "1.1",
          letterSpacing: "-0.02em",
        }}
      >
        {title}
      </h2>
      {children}
    </motion.section>
  );
});

DocSection.displayName = "DocSection";

// StepCard Component
function StepCard({
  stepNumber,
  title,
  code,
}: {
  stepNumber: string;
  title: string;
  code: string;
}) {
  return (
    <div className="mb-8">
      <div className="flex items-baseline gap-4 mb-4">
        <span
          className="text-sm font-mono uppercase tracking-[0.15em] text-[var(--system-cyan)]"
        >
          Step {stepNumber}
        </span>
        <h3
          className="text-xl md:text-2xl font-semibold"
          style={{
            lineHeight: "1.3",
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </h3>
      </div>
      <CodeBlock code={code} />
    </div>
  );
}

// CodeBlock Component
function CodeBlock({ code, language: _language = "javascript" }: { code: string; language?: string }) {
  return (
    <div className="relative w-full max-w-full">
      <pre
        className="p-4 sm:p-6 bg-[var(--surface-2)] border border-[var(--border)] overflow-x-auto text-xs sm:text-sm font-mono max-w-full"
        style={{ lineHeight: "1.6" }}
      >
        <code className="text-[var(--foreground)] block">{code}</code>
      </pre>
    </div>
  );
}

// SDKCard Component
function SDKCard({
  title,
  packageName,
  features,
}: {
  title: string;
  packageName: string;
  features: string[];
}) {
  return (
    <div className="relative border border-[var(--border)] bg-[var(--surface-1)] p-6">
      {/* Top accent line */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-[var(--system-cyan)]" />

      <h3
        className="text-xl md:text-2xl font-semibold mb-2"
        style={{
          lineHeight: "1.3",
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </h3>
      <p className="text-sm font-mono text-[var(--system-cyan)] mb-4">{packageName}</p>

      <ul className="space-y-2">
        {features.map((feature, index) => (
          <li
            key={index}
            className="flex items-start gap-2 text-sm text-[var(--muted-foreground)]"
            style={{ lineHeight: "1.6" }}
          >
            <span className="w-1.5 h-1.5 bg-[var(--system-cyan)] mt-2 flex-shrink-0" />
            {feature}
          </li>
        ))}
      </ul>
    </div>
  );
}

// EndpointCard Component
function EndpointCard({
  method,
  endpoint,
  description,
}: {
  method: string;
  endpoint: string;
  description: string;
}) {
  return (
    <div className="relative border border-[var(--border)] bg-[var(--surface-1)] p-4 sm:p-5 w-full max-w-full">
      {/* Top accent line */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-[var(--system-cyan)]" />

      <div className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-3 mb-2">
        <span
          className="px-2 py-1 text-xs font-mono uppercase border inline-block w-fit"
          style={{
            borderColor: "var(--confidence)",
            color: "var(--confidence)",
            backgroundColor: "rgba(34, 197, 94, 0.1)",
          }}
        >
          {method}
        </span>
        <code className="text-xs sm:text-sm font-mono text-[var(--foreground)] break-all">{endpoint}</code>
      </div>
      <p
        className="text-sm text-[var(--muted-foreground)]"
        style={{ lineHeight: "1.6" }}
      >
        {description}
      </p>
    </div>
  );
}